// Upload page — /<slug>/upload
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { ArrowLeft, X, Plus, FolderPlus, UploadCloud, Play } from "lucide-react";
import PageShell from "../components/PageShell";
import ShareAlbumModal from "../components/ShareAlbumModal";
import FaceRegisterModal from "../components/FaceRegisterModal";
import PasscodeGate from "../components/PasscodeGate";
import { Albums, Subfolders, uploadFiles, uploadFileChunked, CHUNKED_THRESHOLD, API } from "../lib/api";
import { extractVideoPoster, uploadMediaThumb } from "../lib/videoPoster";
import CloudImportButtons from "../components/CloudImportButtons";
import useAutoJoin from "../hooks/useAutoJoin";
import { useAuth } from "../lib/AuthContext";
import { getGuestId } from "../lib/guestId";

const MAX_VIDEO_BYTES = 1024 * 1024 * 1024; // 1 GB — chunked uploads
const MAX_IMAGE_BYTES = 50 * 1024 * 1024;   // 50 MB

const NAME_KEY = "meyecam.display_name";

export default function UploadPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const [album, setAlbum] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploaderName, setUploaderName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]); // [{file, preview, progress}]
  const [adding, setAdding] = useState(0); // number of files currently being processed by the picker
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(""); // "Starting…", "Uploading…", "Finalizing…"
  const [currentFile, setCurrentFile] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [faceRegisterOpen, setFaceRegisterOpen] = useState(false);
  // Have we already prompted the guest to register on this device? Remember by album.
  const FACE_PROMPTED_KEY = `meyecam.face_prompted.${slug}`;
  const [lastUploaded, setLastUploaded] = useState(0); // count for the celebration copy

  useAutoJoin(album); // signed-in visitors get this album on their dashboard

  useEffect(() => {
    setUploaderName(localStorage.getItem(NAME_KEY) || "");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const load = async () => {
    try {
      const a = await Albums.get(slug);
      setAlbum(a);
      // Locked + not unlocked yet → don't fetch gated resources (they'd 401)
      if (a.requires_passcode && !a.owner_id) return;
      const sf = await Subfolders.listMine(slug, getGuestId()).catch(() => []);
      setFolders(sf);
    } catch {
      toast.error("Album not found");
      navigate("/");
    }
  };

  const onDrop = (accepted) => {
    if (!accepted || accepted.length === 0) return;
    const ok = [];
    const rejected = [];
    accepted.forEach((f) => {
      const isVideo = f.type.startsWith("video/");
      const cap = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (f.size > cap) {
        rejected.push({ name: f.name, mb: Math.round(f.size / (1024 * 1024)), isVideo });
      } else {
        ok.push(f);
      }
    });
    if (rejected.length) {
      rejected.forEach((r) =>
        toast.error(
          r.isVideo
            ? `${r.name} is ${r.mb} MB — videos must be ≤ 1 GB. Try lower quality or trim it.`
            : `${r.name} is ${r.mb} MB — images must be ≤ 50 MB.`
        )
      );
    }

    // 1. PHASE ONE — synchronous, instant: drop tiles into the grid with no preview
    //    so the user sees immediate feedback. createObjectURL() and video frame
    //    extraction happen lazily in phase two below.
    const mapped = ok.map((f) => ({
      file: f,
      preview: null,
      previewPending: true,
      isVideo: f.type.startsWith("video/"),
      id: `${f.name}-${f.size}-${f.lastModified}`,
    }));
    let newCount = 0;
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => p.id));
      const novel = mapped.filter((m) => !seen.has(m.id));
      newCount = novel.length;
      return [...prev, ...novel];
    });
    if (newCount > 0) setAdding((n) => n + newCount);

    // 2. PHASE TWO — yield to the browser, then generate previews in small batches
    //    so the main thread never freezes (this is what made the UI lock up for 30s
    //    when the user dropped 50+ photos).
    const pendingNovel = mapped; // we'll re-filter below
    setTimeout(() => generatePreviewsInBatches(pendingNovel), 0);
  };

  // Lazily generate previews so picking 50+ files doesn't freeze the main thread.
  // - Images: cheap — URL.createObjectURL() only.
  // - Videos: actually decodes a frame via <video> + canvas. We do these one at a time.
  //   The decoded blob is stashed on the file row so we don't re-decode at upload time.
  const generatePreviewsInBatches = async (items) => {
    const BATCH = 8; // images per macrotask
    for (let i = 0; i < items.length; i += BATCH) {
      const slice = items.slice(i, i + BATCH);
      // Resolve images first — they're free
      const imageUpdates = slice
        .filter((it) => !it.isVideo)
        .map((it) => ({ id: it.id, preview: URL.createObjectURL(it.file) }));
      if (imageUpdates.length) {
        setFiles((prev) =>
          prev.map((p) => {
            const u = imageUpdates.find((x) => x.id === p.id);
            return u ? { ...p, preview: u.preview, previewPending: false } : p;
          })
        );
        setAdding((n) => Math.max(0, n - imageUpdates.length));
      }
      // Resolve videos one by one — frame extraction is async + GPU-heavy
      for (const it of slice.filter((x) => x.isVideo)) {
        const blob = await extractVideoPoster(it.file).catch(() => null);
        const url = blob ? URL.createObjectURL(blob) : null;
        setFiles((prev) =>
          prev.map((p) =>
            p.id === it.id ? { ...p, preview: url, posterBlob: blob, previewPending: false } : p
          )
        );
        setAdding((n) => Math.max(0, n - 1));
      }
      // Yield between batches so the browser can paint + handle input
      await new Promise((r) => setTimeout(r, 0));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [], "video/*": [] },
    multiple: true,
  });

  const removeFile = (id) =>
    setFiles((prev) => prev.filter((p) => p.id !== id));

  const onCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const f = await Subfolders.create(slug, newFolderName.trim(), getGuestId());
      setFolders((prev) => [...prev, f]);
      setSelectedFolder(f.id);
      setNewFolderName("");
      setShowFolderModal(false);
      toast.success("Folder created");
    } catch {
      toast.error("Could not create folder");
    }
  };

  const onUpload = async () => {
    if (!uploaderName.trim()) { toast.error("Please add your name"); return; }
    if (files.length === 0) { toast.error("Select files to upload"); return; }
    localStorage.setItem(NAME_KEY, uploaderName.trim());
    setUploading(true);
    setProgress(0);
    setPhase("Preparing upload…");
    setCurrentFile("");

    const total = files.length;
    const items = [...files];
    let succeeded = 0;
    let doneCount = 0;
    const failures = []; // [{ name, reason }]
    const guestId = getGuestId();
    const trimmedName = uploaderName.trim();
    const trimmedTitle = title.trim() || undefined;
    const trimmedDesc = description.trim() || undefined;

    // Per-file % map → overall progress (works with parallel uploads)
    const filePct = {};
    const updateOverall = () => {
      let sum = 0;
      for (const it of items) sum += filePct[it.id] || 0;
      setProgress(Math.round(sum / Math.max(1, total)));
    };

    const explain = (err, fallback = "Upload failed") => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (detail) return detail;
      if (status === 413) return "Too large for the server";
      if (status === 409) return "Name already taken in this album";
      if (status === 403) return "Not allowed in that folder";
      if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) return "Upload timed out (slow connection)";
      if (err?.message === "Network Error" || !status) return "Network dropped";
      return `${fallback} (${status || "unknown"})`;
    };

    // Upload one file end-to-end (always original quality): upload → video poster.
    const runOne = async (item) => {
      const file = item.file;
      const isVideo = file.type.startsWith("video/");
      const sizeMB = Math.max(1, Math.round(file.size / (1024 * 1024)));
      const tid = toast.loading(`Uploading ${file.name} (${sizeMB} MB)…`);
      const onProg = (p) => { filePct[item.id] = Math.min(99, p); updateOverall(); };
      try {
        if (file.size >= CHUNKED_THRESHOLD) {
          const result = await uploadFileChunked(slug, file, {
            uploaderName: trimmedName,
            subfolderId: selectedFolder,
            title: trimmedTitle,
            description: trimmedDesc,
            guestId,
            onProgress: onProg,
            onPhase: (p) => {
              if (p === "finalizing") setPhase("Finalizing large file…");
              else if (p === "done") setPhase("Uploading…");
            },
            onResume: ({ have, of }) => {
              if (have > 0) {
                toast.message(`Resuming ${file.name} — ${have}/${of} chunks already uploaded`, { duration: 4000 });
              }
            },
          });
          // Video? upload the pre-extracted poster (or extract now as a fallback)
          if (result?.id && isVideo) {
            const blob = item.posterBlob || (await extractVideoPoster(item.file).catch(() => null));
            if (blob) await uploadMediaThumb(result.id, blob);
          }
        } else {
          const res = await uploadFiles(slug, {
            uploaderName: trimmedName,
            subfolderId: selectedFolder,
            title: trimmedTitle,
            description: trimmedDesc,
            guestId,
            files: [file],
            onProgress: onProg,
          });
          // For small videos in the single-shot path, generate a poster too
          if (isVideo && res?.uploaded) {
            try {
              const list = await fetch(`${API}/albums/${slug}/media`).then((r) => r.json());
              const match = list.find((m) => m.filename === file.name && m.is_video);
              if (match) {
                const blob = item.posterBlob || (await extractVideoPoster(item.file).catch(() => null));
                if (blob) await uploadMediaThumb(match.id, blob);
              }
            } catch (_) { /* non-blocking */ }
          }
        }
        succeeded += 1;
      } catch (e) {
        const reason = explain(e);
        failures.push({ name: file.name, reason });
        toast.error(`${file.name}: ${reason}`, { duration: 6000 });
      } finally {
        toast.dismiss(tid);
        filePct[item.id] = 100;
        doneCount += 1;
        setCurrentFile(`${doneCount}/${total} done`);
        updateOverall();
      }
    };

    try {
      setPhase("Uploading…");
      // 3 files in flight at once — much faster than one-by-one on mobile.
      const CONCURRENCY = 3;
      let cursor = 0;
      const worker = async () => {
        for (;;) {
          const i = cursor++;
          if (i >= items.length) return;
          await runOne(items[i]);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, items.length) }, () => worker())
      );

      // Summary
      if (succeeded > 0 && failures.length === 0) {
        toast.success(`Uploaded ${succeeded} file${succeeded === 1 ? "" : "s"}!`);
      } else if (succeeded > 0 && failures.length > 0) {
        toast.success(
          `Uploaded ${succeeded} of ${total} · ${failures.length} failed (${failures.slice(0, 3).map((f) => f.name).join(", ")}${failures.length > 3 ? "…" : ""})`,
          { duration: 8000 }
        );
      } else {
        toast.error(`No files uploaded — all ${total} failed. Check sizes and try again.`, { duration: 8000 });
      }

      files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      setFiles([]);
      setPhase("");
      setCurrentFile("");
      if (succeeded > 0) {
        // Show the celebration/share moment — every contributor becomes a distributor.
        setLastUploaded(succeeded);
        setShareOpen(true);
        // Offer face registration once per device per album.
        try {
          const alreadyPrompted = localStorage.getItem(FACE_PROMPTED_KEY);
          if (!alreadyPrompted && (uploaderName || "").trim()) {
            localStorage.setItem(FACE_PROMPTED_KEY, "1");
            // slight delay so the share modal appears first
            setTimeout(() => setFaceRegisterOpen(true), 800);
          }
        } catch (_) { /* storage full */ }
      }
    } catch (e) {
      // Unexpected error outside per-file loop — shouldn't happen in normal flow
      console.error("Upload error:", e);
      toast.error("Something went wrong starting the upload — please try again.");
    } finally {
      setUploading(false);
    }
  };

  const totalSize = useMemo(
    () => files.reduce((s, f) => s + f.file.size, 0),
    [files]
  );

  if (!album) {
    return <PageShell><div className="flex justify-center mt-24"><div className="meye-spinner" /></div></PageShell>;
  }

  // Locked album + no valid unlock token → show the gate before any upload UI.
  if (album.requires_passcode && !album.owner_id) {
    return <PasscodeGate album={album} onUnlocked={() => load()} />;
  }

  return (
    <PageShell
      headerLeft={
        <button
          onClick={() => navigate(`/${slug}`)}
          className="meye-chip"
          style={{ padding: "8px 12px" }}
          data-testid="back-to-landing"
        >
          <ArrowLeft size={14} /> Back
        </button>
      }
    >
      <div className="mt-2 mb-5 text-center">
        <div className="section-label">UPLOAD TO</div>
        <div className="text-2xl font-semibold tracking-tight mt-1" data-testid="upload-album-name">{album.name}</div>
      </div>

      <div className="glass-card p-5">
        <label className="section-label block mb-2">YOUR NAME</label>
        <input
          className="meye-input mb-1"
          placeholder="e.g. Priya"
          value={uploaderName}
          onChange={(e) => setUploaderName(e.target.value)}
          data-testid="uploader-name-input"
          maxLength={50}
        />
        <div className="text-xs mb-5" style={{ color: "rgba(255,255,255,0.4)" }}>
          Reserved to you on this device — others can&apos;t upload using the same name.
        </div>

        <label className="section-label block mb-2">TITLE (OPTIONAL)</label>
        <input
          className="meye-input mb-4"
          placeholder='e.g. "Cocktail hour" — applies to all files in this upload'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={140}
          data-testid="upload-title-input"
        />

        <label className="section-label block mb-2">DESCRIPTION (OPTIONAL)</label>
        <textarea
          className="meye-input mb-5"
          placeholder="A few words about what's in these photos…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          rows={3}
          style={{ resize: "vertical", minHeight: 80, fontFamily: "inherit" }}
          data-testid="upload-description-input"
        />

        <div className="flex items-center justify-between mb-2">
          <div className="section-label">FOLDER (OPTIONAL)</div>
          <button
            className="meye-chip"
            style={{ padding: "6px 10px", fontSize: 12 }}
            onClick={() => setShowFolderModal(true)}
            data-testid="new-folder-btn"
          >
            <FolderPlus size={12} /> New
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            className={`meye-chip ${selectedFolder === null ? "active" : ""}`}
            style={selectedFolder === null ? { "--chip-active-bg": "rgba(127,75,255,0.18)", "--chip-active-border": "#7F4BFF" } : {}}
            onClick={() => setSelectedFolder(null)}
            data-testid="folder-chip-none"
          >
            All
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              className={`meye-chip ${selectedFolder === f.id ? "active" : ""}`}
              style={selectedFolder === f.id ? { "--chip-active-bg": "rgba(127,75,255,0.18)", "--chip-active-border": "#7F4BFF" } : {}}
              onClick={() => setSelectedFolder(f.id)}
              data-testid={`folder-chip-${f.id}`}
            >
              {f.name}
            </button>
          ))}
        </div>

        <div
          {...getRootProps()}
          className="rounded-2xl p-6 text-center cursor-pointer transition"
          style={{
            border: `2px dashed ${isDragActive ? "#22E2FF" : "rgba(255,255,255,0.18)"}`,
            background: isDragActive ? "rgba(34,226,255,0.06)" : "rgba(255,255,255,0.02)",
          }}
          data-testid="dropzone"
        >
          <input {...getInputProps()} data-testid="file-input" />
          <UploadCloud size={28} style={{ margin: "0 auto", color: "#7F4BFF" }} />
          <div className="mt-2 font-medium">Tap to select or drop files</div>
          <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>
            Images & videos · multi-select supported
          </div>
          <div className="text-[11px] mt-2" style={{ color: "rgba(255,255,255,0.35)" }}>
            Max 50 MB per image · 1 GB per video
          </div>
        </div>

        {/* Cloud imports — Google Photos / Drive / Dropbox */}
        <CloudImportButtons onFiles={onDrop} />

        {files.length > 0 && (
          <>
            <div className="flex items-center justify-between mt-5 mb-3">
              <div className="section-label">
                {files.length} SELECTED · {(totalSize/1024/1024).toFixed(1)} MB
                {adding > 0 && (
                  <span className="ml-2" style={{ color: "#22E2FF" }}>
                    · preparing {adding}…
                  </span>
                )}
              </div>
              <button
                className="text-xs"
                style={{ color: "#FF6B6B" }}
                onClick={() => { files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)); setFiles([]); setAdding(0); }}
                data-testid="clear-files"
              >
                Clear
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="file-grid">
              {files.map((f) => (
                <div key={f.id} className="relative rounded-xl overflow-hidden" style={{ aspectRatio: "1/1", background: "rgba(255,255,255,0.05)" }}>
                  {f.preview ? (
                    <>
                      <img
                        src={f.preview}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      {f.isVideo && (
                        <div
                          className="absolute bottom-1 left-1 rounded-md flex items-center gap-1 px-1.5 py-0.5"
                          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
                        >
                          <Play size={10} color="#22E2FF" fill="#22E2FF" />
                          <span className="text-[10px] font-medium" style={{ color: "#fff" }}>VIDEO</span>
                        </div>
                      )}
                    </>
                  ) : f.previewPending ? (
                    // Skeleton — animated shimmer so users see "yes, I got your tap"
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 meye-skeleton">
                      <div className="meye-spinner-sm" />
                      <div className="text-[10px]" style={{ color: "rgba(255,255,255,0.55)" }}>
                        {f.isVideo ? "Reading video…" : "Loading…"}
                      </div>
                    </div>
                  ) : (
                    // Final fallback (e.g. browser can't decode the codec)
                    <div className="flex flex-col items-center justify-center h-full gap-1 px-2 text-center">
                      <Play size={20} color="#22E2FF" />
                      <div className="text-[10px] leading-tight" style={{ color: "rgba(255,255,255,0.7)" }}>
                        {f.file.name.length > 18 ? f.file.name.slice(0, 16) + "…" : f.file.name}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => removeFile(f.id)}
                    className="absolute top-1 right-1 rounded-full p-1"
                    style={{ background: "rgba(0,0,0,0.6)" }}
                    data-testid={`remove-file-${f.id}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {uploading && (
          <div className="mt-4">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#7F4BFF,#22E2FF)" }} />
            </div>
            <div className="flex justify-between items-center mt-2 text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
              <span className="truncate flex-1 mr-2">
                {phase || "Working…"}
                {currentFile && ` · ${currentFile}`}
              </span>
              <span className="font-medium" style={{ color: "#22E2FF" }}>{progress}%</span>
            </div>
          </div>
        )}

        <button
          className="cta-pill w-full mt-5"
          onClick={onUpload}
          disabled={uploading || files.length === 0}
          data-testid="upload-btn"
        >
          {uploading ? <div className="meye-spinner" /> : <>Upload {files.length > 0 ? `${files.length} file${files.length === 1 ? "" : "s"}` : "files"}</>}
        </button>
      </div>

      {/* Create your own footer */}
      <div className="mt-10 text-center">
        <div className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
          Hosting your own event?
        </div>
        <button
          onClick={() => (user ? navigate("/") : signIn())}
          className="mt-1 font-bold text-[15px]"
          style={{ color: "#22E2FF" }}
          data-testid="create-your-own-cta-upload"
        >
          Create your own Event →
        </button>
      </div>

      {showFolderModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setShowFolderModal(false)}>
          <div className="glass-card p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-semibold mb-3">New folder</div>
            <input
              className="meye-input"
              placeholder="Folder name (e.g. Ceremony)"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              autoFocus
              maxLength={40}
              data-testid="new-folder-input"
            />
            <div className="flex gap-2 mt-4">
              <button className="meye-chip flex-1 justify-center" onClick={() => setShowFolderModal(false)}>Cancel</button>
              <button className="cta-pill flex-1" onClick={onCreateFolder} data-testid="confirm-new-folder">Create</button>
            </div>
          </div>
        </div>
      )}

      <ShareAlbumModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onView={() => { setShareOpen(false); navigate(`/${slug}/gallery`); }}
        albumName={album.name}
        slug={slug}
        uploadedCount={lastUploaded}
        uploaderName={uploaderName.trim()}
      />

      <FaceRegisterModal
        open={faceRegisterOpen}
        onClose={() => setFaceRegisterOpen(false)}
        slug={slug}
        guestId={getGuestId()}
        name={uploaderName.trim()}
      />
    </PageShell>
  );
}
